/**
 * El explicador del anclaje de la cuenta corriente.
 *
 * QUE PROBLEMA RESUELVE ESTE ARCHIVO
 * ----------------------------------
 * En la base esto son dos campos con nombres de contador (`cc_modo`,
 * `cc_moneda`) y tres letras de diferencia entre una cuenta que se actualiza
 * sola y una que no. La persona que da de alta un cliente no sabe contabilidad,
 * y esta eligiendo algo que despues NO se puede cambiar.
 *
 * Asi que no se le pregunta el modo: se le pregunta la moneda de la deuda, que
 * es como lo dice cualquiera en el mostrador, y debajo va UNA linea con la
 * consecuencia de lo elegido. Antes eran dos tarjetas con titulo, bajada y un
 * tooltip con un ejemplo en numeros; se leia como un examen y nadie pasaba el
 * mouse por el tooltip. Nada de "recomendado": el que sabe como le paga cada
 * cliente es el dueño.
 */
import { ArrowRight, Lock } from "lucide-react";

import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Card } from "@/components/ui/card";
import { Segmented } from "@/components/ui/segmented";
import { cn } from "@/lib/utils";

import type { Anclaje, ImpactoAnclaje } from "./logica";

/**
 * Lo que pasa con la deuda si el dolar sube. Es la misma frase en la pregunta y
 * en el resumen de antes de crear: lo que se eligio y lo que se confirma tienen
 * que decir lo mismo con las mismas palabras.
 */
export const CONSECUENCIA_ANCLAJE: Record<Anclaje, string> = {
  PESOS: "Si el dólar sube, sigue debiendo lo mismo.",
  DOLAR: "Si el dólar sube, debe más pesos.",
};

const OPCIONES = [
  { valor: "PESOS", etiqueta: "En pesos" },
  { valor: "DOLAR", etiqueta: "En dólares" },
] as const;

export interface PreguntaAnclajeProps {
  /** `null`: ninguna marcada todavia, hay que elegir. */
  valor: Anclaje | null;
  onChange: (a: Anclaje) => void;
  /** Cuando esta bloqueado, los botones se ven pero no se tocan. */
  bloqueado?: boolean;
  className?: string;
}

/**
 * La pregunta y sus dos respuestas: un `Segmented` y una linea abajo con la
 * consecuencia de lo elegido. Arranca sin ninguna marcada a proposito.
 */
export function PreguntaAnclaje({
  valor,
  onChange,
  bloqueado = false,
  className,
}: PreguntaAnclajeProps): JSX.Element {
  return (
    <div className={cn("space-y-2", className)}>
      <p className="text-sm font-semibold">Su deuda, ¿en pesos o en dólares?</p>
      <Segmented
        opciones={OPCIONES}
        valor={valor}
        onChange={onChange}
        etiqueta="Su deuda, ¿en pesos o en dólares?"
        tamano="md"
        disabled={bloqueado}
      />
      <p className="text-xs text-muted-foreground">
        {valor ? CONSECUENCIA_ANCLAJE[valor] : "Se puede cambiar hasta su primera venta a cuenta."}
      </p>
    </div>
  );
}

export interface AnclajeBloqueadoProps {
  movimientos: number;
  anclaje: Anclaje;
  /** Los dos numeros del mismo historial. `null` si la cuenta esta en cero. */
  impacto: ImpactoAnclaje | null;
  className?: string;
}

/**
 * Por que no se puede cambiar, con el numero puesto.
 *
 * "No se puede cambiar" solo, sin explicacion, se lee como una limitacion del
 * sistema y lo unico que produce es un llamado preguntando como se hace. Con
 * los dos numeros al lado se lee como lo que es: la deuda cambiaria de valor
 * sin que nadie haya comprado ni pagado nada.
 */
export function AnclajeBloqueado({
  movimientos,
  anclaje,
  impacto,
  className,
}: AnclajeBloqueadoProps): JSX.Element {
  const actual = anclaje === "DOLAR" ? "anclada al dólar" : "en pesos";
  const otro = anclaje === "DOLAR" ? "en pesos" : "anclada al dólar";

  return (
    <Card
      variant="muted"
      padding="normal"
      role="note"
      className={cn("space-y-3 text-xs leading-relaxed", className)}
    >
      <p className="flex items-start gap-2 text-sm font-medium text-foreground">
        <Lock className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <span>
          Esta cuenta ya tiene {movimientos} {movimientos === 1 ? "movimiento" : "movimientos"}. El
          anclaje quedó fijo.
        </span>
      </p>

      <p className="text-muted-foreground">
        El saldo no está guardado en ninguna parte: se calcula sumando todo el historial, y se suma
        distinto según el anclaje. Cambiarlo ahora no migra nada — vuelve a leer las mismas ventas
        con otra vara.
      </p>

      {impacto ? (
        <div className="space-y-2 border-t border-border pt-3">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-muted-foreground">Hoy, la cuenta {actual} dice que debe</span>
            <Importe monto={impacto.hoyArs} moneda="ARS" tamano="sm" />
          </div>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <ArrowRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="text-muted-foreground">El mismo historial, {otro}, diría</span>
            <Importe monto={impacto.otroArs} moneda="ARS" tamano="sm" />
          </div>
          <Aviso tono="warning" compacto>
            Una diferencia de{" "}
            <Importe monto={Math.abs(impacto.diferenciaArs)} moneda="ARS" tamano="sm" /> sin que
            nadie haya comprado ni pagado nada en el medio.
          </Aviso>
        </div>
      ) : null}

      <div className="space-y-1">
        <p className="font-medium text-foreground">Qué hacer en su lugar</p>
        <p className="text-muted-foreground">
          {/* Antes mandaba a hacer «un ajuste» desde Clientes, y esa pantalla no
              tiene ningun boton de ajuste: la salida que se ofrecia no existia. */}
          La forma prolija de pasar la cuenta al otro anclaje es con dos asientos: un ajuste que
          cierre el saldo de hoy y otro que lo vuelva a abrir con el anclaje nuevo. Esos ajustes
          todavía no se pueden cargar desde el sistema, así que por ahora la cuenta sigue{" "}
          {anclaje === "DOLAR" ? "anclada al dólar" : "fija en pesos"}.
        </p>
      </div>
    </Card>
  );
}
